import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppleLogin, useGoogleLogin } from "@/shared/api/queries";
import { useSessionStore } from "@/shared/session";
import { useToast } from "@/shared/components/Toast";
import { useTranslation } from "@/shared/i18n/I18nProvider";
import { parseCallbackFromHash } from "@/shared/oauth";

/**
 * OAuth redirect-flow callback.
 *
 * Google sends the id_token back via URL hash (`#id_token=...`). This page:
 *   1. Parses the hash + verifies nonce echoed back.
 *   2. POSTs the id_token to our Worker `/v1/auth/google/login`.
 *   3. Persists the resulting session and navigates to `returnTo`
 *      (default `/dashboard`).
 *
 * Errors (user cancelled, nonce mismatch, Worker rejection) toast + redirect
 * back to `/sign-in`.
 *
 * SignIn.tsx initiates the flow by `window.location.href = buildGoogleAuthorizeUrl(...)`.
 */
export function OAuthCallback() {
  const navigate = useNavigate();
  const setSession = useSessionStore((s) => s.setSession);
  const toast = useToast();
  const { locale, t } = useTranslation();
  const google = useGoogleLogin();
  const apple = useAppleLogin();
  const [status, setStatus] = useState<"working" | "error">("working");
  const ranRef = useRef(false);

  useEffect(() => {
    // StrictMode runs effects twice in dev; guard so we don't double-call
    // /v1/auth/google/login on the same id_token (Worker handles idempotency
    // on user upsert but the network round-trip is wasteful).
    if (ranRef.current) return;
    ranRef.current = true;

    const result = parseCallbackFromHash(window.location.hash);
    // Clean the URL hash regardless so a back-navigation doesn't replay.
    window.history.replaceState(null, "", window.location.pathname);

    if (!result) {
      setStatus("error");
      toast.error(t("oauthCallback.errorMissing"));
      navigate("/sign-in", { replace: true });
      return;
    }
    if (result.error) {
      setStatus("error");
      toast.error(t("oauthCallback.errorWithCode", { code: result.error }));
      navigate("/sign-in", { replace: true });
      return;
    }

    void (async () => {
      try {
        // Dispatch to the right backend endpoint based on which provider
        // initiated the redirect (set by build{Google,Apple}AuthorizeUrl).
        const mutation = result.provider === "apple" ? apple : google;
        const res = await mutation.mutateAsync({
          id_token: result.idToken,
          locale,
        });
        setSession({
          accessToken: res.access_token,
          refreshToken: res.refresh_token,
          userId: res.user.id,
          workspaceId: res.user.default_workspace_id,
        });
        navigate(result.returnTo, { replace: true });
      } catch (err) {
        setStatus("error");
        toast.error(
          t("oauthCallback.errorExchange", {
            error: err instanceof Error ? err.message : "—",
          }),
        );
        navigate("/sign-in", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-6">
      <div className="text-center">
        {status === "working" ? (
          <>
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-subtle border-t-primary" />
            <p className="mt-4 text-sm font-bold text-fg">
              {t("oauthCallback.working")}
            </p>
          </>
        ) : (
          <p className="text-sm text-fg-subtle">
            {t("oauthCallback.redirecting")}
          </p>
        )}
      </div>
    </div>
  );
}
